import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import Accordion from "../../components/Accordion";
import { brokers as staticBrokers } from "../../config/staticData";
import { colors } from "../../config/colors";
import { images, svg } from "../../config/images";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  getFaqs,
  getBrokers,
  getPlatformStates,
  getWhyChooseUs,
  getAbout,
  getCourses as apiGetCourses,
  getMyCoursePurchases,
  apiUrl,
} from "../../api";
import { useAuth } from "../../context/AuthContext";
import { purchaseOrEnrollCourse } from "../../utils/courseCheckout";
import CourseCard from "../../components/CourseCard";
import PageLoader from "../../components/PageLoader";
import Dialog from "../../components/Dialog";

const Landing = () => {
  const navigate = useNavigate();
  const { user, enrollInCourse, refreshUser } = useAuth();
  const [faqItems, setFaqItems] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [enrolledSet, setEnrolledSet] = useState(new Set());
  const [dbPurchasedSet, setDbPurchasedSet] = useState(new Set());
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [courseReviews, setCourseReviews] = useState([]);
  const [broker1, setBroker1] = useState("");
  const [broker2, setBroker2] = useState("");
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoVideo, setDemoVideo] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [niftyData, setNiftyData] = useState({
    price: "Loading...",
    change: "--",
    isPositive: true,
    high: "--",
    low: "--",
    volume: "--",
    avgReturn: "--",
    successRate: "95%",
  });
  const [platformStates, setPlatformStates] = useState(null);
  const [whyChooseUs, setWhyChooseUs] = useState({
    title: "Why Market Mavericks?",
    image: images.women,
    points: [
      {
        title: "100% Practical",
        description:
          "Learn through real-world examples and hands-on trading scenarios. No theory, only actionable strategies.",
      },
      {
        title: "Even a Kid Can Understand",
        description:
          "Complex financial concepts explained in simple, easy-to-understand language. Perfect for beginners.",
      },
      {
        title: "Modern Investing Techniques",
        description:
          "Stay ahead with the latest trading strategies, tools, and technologies used by professional traders.",
      },
    ],
  });
  const selectedBroker1 = brokers.find((b) => b.name === broker1);
  const selectedBroker2 = brokers.find((b) => b.name === broker2);

  // Format numeric stat for hero/bottom sections (e.g. 50000 -> "50K+", 2.5e7 -> "2.5Cr+")
  const formatStat = (value, type = "default") => {
    if (value == null || value === "")
      return type === "volume" ? "₹2.5Cr+" : "—";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    if (type === "volume") return `₹${(n / 10000000).toFixed(1)}Cr+`;
    if (n >= 1000000) return `${(n / 1000000).toFixed(0)}M+`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K+`;
    return `${n}+`;
  };
  const stat = (key, fallback) =>
    platformStates && platformStates[key] != null
      ? typeof platformStates[key] === "number"
        ? formatStat(
            platformStates[key],
            key === "total_trading_volume" ? "volume" : "default",
          )
        : String(platformStates[key])
      : fallback;

  useEffect(() => {
    const loadBrokers = async () => {
      try {
        const list = await getBrokers();

        if (list && list.length) {
          setBrokers(list);

          // set default selected brokers
          setBroker1(list[0]?.name);
          setBroker2(list[1]?.name || list[0]?.name);
        }
      } catch (error) {
        console.error("Failed to load brokers", error);
      }
    };

    loadBrokers();
  }, []);

  const loadPlatformStates = async () => {
    try {
      const data = await getPlatformStates();
      if (data) setPlatformStates(data);
    } catch {
      // keep null, UI uses fallbacks
    }
  };

  useEffect(() => {
    loadPlatformStates();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getAbout();
        if (mounted && data) {
          setDemoVideo({
            videoUrl: data.demoVideoUrl,
            title: data.demoVideoTitle,
            description: data.demoVideoDesc,
          });
        }
      } catch {
        // use fallback
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const apiFaqs = await getFaqs();
        if (!mounted) return;
        setFaqItems(Array.isArray(apiFaqs) ? apiFaqs : []);
      } catch {
        if (mounted) setFaqItems([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getWhyChooseUs();
        if (!mounted) return;
        if (data && typeof data === "object") {
          setWhyChooseUs((prev) => ({
            title: data.title || prev.title,
            image: data.image || prev.image,
            points:
              Array.isArray(data.points) && data.points.length > 0
                ? data.points
                : prev.points,
          }));
        }
      } catch {
        // use fallback state
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingCourses(true);
        const data = await apiGetCourses();
        if (!mounted) return;

        const mapped = (Array.isArray(data) ? data : []).map((c) => ({
          ...c,
          bannerText: c.bannerText || c.title,
          duration: c.duration || "6 hrs 30 min",
          lessons: Array.isArray(c.lessons)
            ? `${c.lessons.length} Lessons`
            : "0 Lessons",
          rating: c.rating || 4.7,
          studentCount: c.studentCount || "6k+",
          studentAvatars: [
            "https://ui-avatars.com/api/?name=S+1&background=random&size=128",
            "https://ui-avatars.com/api/?name=S+2&background=random&size=128",
            "https://ui-avatars.com/api/?name=S+3&background=random&size=128",
            "https://ui-avatars.com/api/?name=S+4&background=random&size=128",
          ],
          price:
            typeof c.price === "number"
              ? `₹${c.price.toLocaleString("en-IN")}`
              : c.price || "₹4,999",
        }));

        setCourses(mapped);

        // Extract reviews from courses for testimonials section
        const allReviews = mapped.reduce((acc, c) => {
          const courseReviews = (Array.isArray(c.reviews) ? c.reviews : []).map(
            (r) => ({
              id: r._id || Math.random().toString(36).substr(2, 9),
              name: r.userName || "Anonymous Student",
              review: r.review || "No review message provided.",
              rating: r.rating || 5,
              role: `Student - ${c.title}`,
              image: `https://ui-avatars.com/api/?name=${encodeURIComponent(r.userName || "S")}&background=38915c&color=fff&size=128`,
            }),
          );
          return [...acc, ...courseReviews];
        }, []);

        // Sort by rating (highest first) and take top 6, or randomize?
        // Let's sort by rating then date if possible, but for now just keep top 6.
        setCourseReviews(
          allReviews.sort((a, b) => b.rating - a.rating).slice(0, 6),
        );
      } catch (err) {
        console.error("Failed to load landing courses", err);
        if (mounted) setCourses([]);
      } finally {
        if (mounted) setLoadingCourses(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setDbPurchasedSet(new Set());
        return;
      }
      try {
        const data = await getMyCoursePurchases();
        if (!cancelled)
          setDbPurchasedSet(
            new Set((data?.purchasedCourseIds || []).map(String)),
          );
      } catch {
        if (!cancelled) setDbPurchasedSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    const fetchNifty = async () => {
      if (cancelled) return;
      try {
        // NSE blocks browser CORS; use our backend proxy on Vercel/local first.
        const shouldUseDirectNse = false;
        let url = shouldUseDirectNse
          ? "https://www.nseindia.com/api/equity-stockIndices?index=NIFTY%2050"
          : apiUrl("/api/nse/nifty");
        let res;
        try {
          res = await fetch(url);
          if (!res.ok) throw new Error("CORS or network issue");
        } catch (e) {
          // Fallback to backend proxy (works in both local and deployed environments)
          url = apiUrl("/api/nse/nifty");
          res = await fetch(url);
        }

        if (res.ok) {
          const rawData = await res.json();
          console.log("FULL API DATA FROM NSE:", rawData); // <- You can see all data here in the browser console!
          const data = rawData?.data?.[0] || rawData?.metadata;
          if (data && !cancelled) {
            const formatValue = (num) => {
              if (num == null) return "--";
              if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
              if (num >= 1000) return (num / 1000).toFixed(1) + "K";
              return num.toString();
            };

            const currentPrice = data.lastPrice ?? data.last ?? 0;
            const changeVal = data.change ?? 0;
            const pChangeVal = data.pChange ?? data.percChange ?? 0;

            setNiftyData({
              price: currentPrice.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              change: `${changeVal > 0 ? "+" : ""}${pChangeVal.toFixed(2)}%`,
              isPositive: changeVal >= 0,
              high: formatValue(data.dayHigh ?? data.high),
              low: formatValue(data.dayLow ?? data.low),
              volume: formatValue(data.totalTradedVolume),
              avgReturn:
                data.perChange365d != null
                  ? `${data.perChange365d > 0 ? "+" : ""}${data.perChange365d}%`
                  : "--",
              successRate:
                data.yearHigh && data.lastPrice
                  ? `${((data.lastPrice / data.yearHigh) * 100).toFixed(1)}%`
                  : "95%",
            });
          }
        } else {
          throw new Error("Proxy also failed");
        }
      } catch (err) {
        console.error("Failed to load Nifty API", err);
        if (!cancelled) {
          setNiftyData((prev) => ({
            ...prev,
            price: "API Error (Restart Backend)",
          }));
        }
      }
    };
    fetchNifty();
    const interval = setInterval(fetchNifty, 60000); // refresh every minute
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleEnrollNow = async (course) => {
    if (!user?.id || !course?.id) return;
    setCheckoutError("");
    setCheckoutLoadingId(course.id);
    try {
      const result = await purchaseOrEnrollCourse({
        course,
        user,
        refreshUser,
        enrollInCourse,
      });
      if (result?.dismissed) return;
      const nextId = String(course.id);
      setEnrolledSet((prev) => new Set(prev).add(nextId));
      setDbPurchasedSet((prev) => new Set(prev).add(nextId));
    } catch (e) {
      setCheckoutError(e?.message || "Could not complete enrollment");
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".broker-dropdown")) {
        setShowDropdown1(false);
        setShowDropdown2(false);
      }
    };

    if (showDropdown1 || showDropdown2) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown1, showDropdown2]);

  // Responsive courses per page
  const [coursesPerPage, setCoursesPerPage] = useState(3);

  useEffect(() => {
    const updateCoursesPerPage = () => {
      if (window.innerWidth >= 2540) setCoursesPerPage(5);
      else if (window.innerWidth >= 1920) setCoursesPerPage(4);
      else setCoursesPerPage(3);
    };
    updateCoursesPerPage();
    window.addEventListener("resize", updateCoursesPerPage);
    return () => window.removeEventListener("resize", updateCoursesPerPage);
  }, []);

  const totalPages = Math.ceil(courses.length / coursesPerPage);

  const nextCourse = () => {
    if (currentCourseIndex < totalPages - 1) {
      setCurrentCourseIndex((prev) => prev + 1);
    } else {
      setCurrentCourseIndex(0);
    }
  };

  const prevCourse = () => {
    if (currentCourseIndex > 0) {
      setCurrentCourseIndex((prev) => prev - 1);
    } else {
      setCurrentCourseIndex(totalPages - 1);
    }
  };

  const StarRating = ({ rating }) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <img
            key={i}
            src={svg.star}
            alt="star"
            className={`w-5 h-5 ${i < rating ? "text-yellow-400" : "text-gray-300"}`}
            style={{ filter: i < rating ? "none" : "opacity(0.3)" }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-50">
      <Header />
      <main className="flex-grow">
        {/* Hero Section - Investment Journey */}
        <section className="relative py-12 md:py-10 md:px-6 lg:px-12 xl:px-4 px-6 overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200 rounded-full opacity-20 blur-3xl animate-pulse"></div>
            <div
              className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-200 rounded-full opacity-20 blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary-100 rounded-full opacity-10 blur-3xl"></div>
          </div>

          <div className="max-w-7xl 4xl:max-w-10xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <div
                className={`text-left transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
              >
                <div className="inline-block mb-4">
                  <span className="bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-semibold">
                    🚀 Start Your Trading Journey
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Begin Your{" "}
                  <span className="text-primary-600 relative">
                    Investment Journey
                    <img
                      src={svg.underline}
                      alt="underline"
                      className="absolute -bottom-2 left-0 w-full h-3 text-primary-300 opacity-50"
                    />
                  </span>{" "}
                  with Our Premium Share Market Courses
                </h1>

                <p className="text-lg md:text-xl 3xl:text-2xl 4xl:text-3xl text-gray-700 mb-8 leading-relaxed">
                  {/* Master the art of stock trading with our comprehensive courses. Learn from industry experts and transform your financial future with practical, real-world strategies. */}
                  {stat("description", " ")}
                </p>

                {/* Key Features */}
                <div className="grid grid-cols-2 gap-4 lg:mb-6 mb-8">
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-primary-100 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                        <img
                          src={svg.chartLine}
                          alt="chart"
                          className="w-6 h-6 text-white"
                        />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary-600">
                          {stat("active_traders", " ")}
                        </div>
                        <div className="text-xs text-gray-600">
                          Active Traders
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-primary-100 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-secondary-500 rounded-lg flex items-center justify-center">
                        <img
                          src={svg.book}
                          alt="book"
                          className="w-6 h-6 text-white"
                        />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-secondary-600">
                          {stat("total_courses", " ")}
                        </div>
                        <div className="text-xs text-gray-600">Courses</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="primary"
                    size="md"
                    icon={svg.arrowRight}
                    iconPosition="right"
                    onClick={() => navigate("/courses")}
                  >
                    Explore Courses
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setDemoModalOpen(true)}
                  >
                    Watch Demo
                  </Button>
                </div>
              </div>

              {/* Right Side - Visual Elements */}
              <div
                className={`relative transition-all duration-1000 pt-8 md:pt-12 lg:pt-16 mb-8 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
              >
                <div className="relative">
                  {/* Main Card */}
                  <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                    <div className="space-y-4">
                      {/* Stock Chart */}
                      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <p className="text-sm text-gray-600">NIFTY 50</p>
                            <p className="text-2xl font-bold text-gray-900">
                              ₹{niftyData.price}
                            </p>
                          </div>
                          <div className="text-right">
                            <p
                              className={`text-sm font-semibold ${niftyData.isPositive ? "text-secondary-600" : "text-red-500"}`}
                            >
                              {niftyData.change}
                            </p>
                            <p className="text-xs text-gray-500">Today</p>
                          </div>
                        </div>
                        <div className="h-32 bg-white rounded-lg p-2">
                          <svg viewBox="0 0 300 120" className="w-full h-full">
                            <polyline
                              points="10,100 50,85 90,70 130,55 170,45 210,40 250,35 290,30"
                              fill="none"
                              stroke="#38915c"
                              strokeWidth="3"
                              className="animate-draw-line"
                            />
                            <polyline
                              points="10,100 50,90 90,75 130,60 170,50 210,45 250,40 290,35"
                              fill="none"
                              stroke="#22c55e"
                              strokeWidth="2"
                              strokeDasharray="5,5"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Trading Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-primary-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Volume</p>
                          <p className="text-lg font-bold text-primary-600">
                            {niftyData.volume}
                          </p>
                        </div>
                        <div className="text-center p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">High</p>
                          <p className="text-lg font-bold text-secondary-600">
                            {niftyData.high}
                          </p>
                        </div>
                        <div className="text-center p-3 bg-primary-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Low</p>
                          <p className="text-lg font-bold text-primary-600">
                            {niftyData.low}
                          </p>
                        </div>
                      </div>

                      {/* Course Highlights */}
                      <div className="space-y-2 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm">
                          <img
                            src={svg.checkmark}
                            alt="check"
                            className="w-5 h-5 text-primary-500"
                          />
                          <span className="text-gray-700">
                            Learn Technical Analysis
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <img
                            src={svg.checkmark}
                            alt="check"
                            className="w-5 h-5 text-primary-500"
                          />
                          <span className="text-gray-700">
                            Master Fundamental Analysis
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <img
                            src={svg.checkmark}
                            alt="check"
                            className="w-5 h-5 text-primary-500"
                          />
                          <span className="text-gray-700">
                            Real-time Market Strategies
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Elements */}
                  <div
                    className="absolute -top-6 md:-top-10 -left-2 md:-left-4 bg-secondary-500 text-white rounded-xl p-3 md:p-4 shadow-xl animate-bounce z-20"
                    style={{ animationDuration: "2s" }}
                  >
                    <div className="text-xl md:text-2xl font-bold">
                      {niftyData.avgReturn}
                    </div>
                    <div className="text-[10px] md:text-xs">Return</div>
                  </div>

                  <div
                    className="absolute -bottom-6 md:-bottom-10 right-2 sm:right-6 md:right-12 lg:right-6 xl:right-1 bg-primary-500 text-white rounded-xl p-3 md:p-4 shadow-xl animate-bounce z-20"
                    style={{
                      animationDuration: "2.5s",
                      animationDelay: "0.5s",
                    }}
                  >
                    <div className="text-xl md:text-2xl font-bold">
                      {niftyData.successRate}
                    </div>
                    <div className="text-[10px] md:text-xs">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Stats Bar */}
            <div className="mt-20 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold text-primary-600 mb-2">
                  {stat("total_trading_volume", " ")}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  Total Reached
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold text-secondary-600 mb-2">
                  {stat("successful_trades", " ")}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  Happy Clients
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold text-primary-600 mb-2">
                  {stat("course_rating", " ")}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  Course Rating
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold text-secondary-600 mb-2">
                  {stat("market_support", " ")}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  Market Support
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Market Mavericks Section */}
        <section className="py-20 bg-gradient-to-b from-primary-50 to-primary-100 relative overflow-hidden">
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Text Content */}
              <div
                className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
              >
                <h2 className="text-4xl md:text-5xl 3xl:text-6xl 4xl:text-7xl font-bold text-gray-900 mb-8">
                  {whyChooseUs.title}
                </h2>

                <div className="space-y-6">
                  {/* Dynamic Feature Points */}
                  {whyChooseUs.points.map((point, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center mt-1">
                        <img
                          src={svg.checkmarkThick}
                          alt="check"
                          className="w-5 h-5 text-white"
                        />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {point.title}
                        </h3>
                        <p className="text-gray-700">{point.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Image with Floating Cards */}
              <div className="relative h-[430px] sm:h-[500px] md:h-[620px] flex items-center justify-center">
                <div className="relative w-full max-w-[520px] h-full flex items-center justify-center mx-auto">
                  {/* Main Image - Dynamic */}
                  <div
                    className={`relative z-30 transition-all duration-1000 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
                  >
                    <div className="w-[210px] h-[270px] sm:w-[260px] sm:h-[340px] md:w-80 md:h-96 rounded-2xl shadow-2xl overflow-hidden border-2 md:border-4 border-white ring-4 sm:ring-6 md:ring-8 ring-white/30">
                      <img
                        src={whyChooseUs.image || images.women}
                        alt="Professional Woman"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                    <div className="absolute -inset-3 rounded-3xl bg-gradient-to-r from-primary-300/20 to-secondary-300/20 blur-xl -z-10"></div>
                  </div>

                  {/* Floating Card 1 - Share Price (Top Left) */}
                  <div className="absolute top-6 sm:top-8 md:top-10 left-2 sm:left-0 sm:-translate-x-[20%] md:-translate-x-[35%] 2xl:-translate-x-[30%] bg-white rounded-lg shadow-xl p-2 sm:p-3 lg:p-4 w-28 sm:w-36 md:w-44 lg:w-48 animate-float z-10">
                    <h4 className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Share Price
                    </h4>
                    <div className="h-10 sm:h-16 md:h-24 bg-gray-50 rounded p-1 sm:p-2">
                      <svg viewBox="0 0 200 100" className="w-full h-full">
                        {/* Grid lines */}
                        <line
                          x1="0"
                          y1="20"
                          x2="200"
                          y2="20"
                          stroke={colors.gray[200]}
                          strokeWidth="1"
                        />
                        <line
                          x1="0"
                          y1="40"
                          x2="200"
                          y2="40"
                          stroke={colors.gray[200]}
                          strokeWidth="1"
                        />
                        <line
                          x1="0"
                          y1="60"
                          x2="200"
                          y2="60"
                          stroke={colors.gray[200]}
                          strokeWidth="1"
                        />
                        <line
                          x1="0"
                          y1="80"
                          x2="200"
                          y2="80"
                          stroke={colors.gray[200]}
                          strokeWidth="1"
                        />
                        <polyline
                          points="10,70 30,60 50,50 70,40 90,35 110,30 130,25 150,20 170,18 190,15"
                          fill="none"
                          stroke="#38915c"
                          strokeWidth="2"
                        />
                        <polyline
                          points="10,75 30,65 50,55 70,45 90,40 110,35 130,30 150,25 170,23 190,20"
                          fill="none"
                          stroke="#22c55e"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                    <div className="flex gap-1 sm:gap-2 mt-1 sm:mt-2 text-[10px] sm:text-xs text-gray-600">
                      <span>M</span>
                      <span>A</span>
                      <span>M</span>
                      <span>J</span>
                      <span>J</span>
                    </div>
                  </div>

                  {/* Floating Card 2 - Market Share (Top Right) */}
                  <div className="absolute top-8 sm:top-10 md:top-12 right-2 sm:right-0 sm:translate-x-[20%] md:translate-x-[35%] 2xl:translate-x-[30%] bg-white rounded-lg shadow-xl p-2 sm:p-3 lg:p-4 w-28 sm:w-36 md:w-44 lg:w-48 animate-float-delayed z-10">
                    <h4 className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Market Share
                    </h4>
                    <div className="h-10 sm:h-16 md:h-24 flex items-center justify-center">
                      <svg
                        viewBox="0 0 100 100"
                        className="w-10 h-10 sm:w-16 sm:h-16 md:w-24 md:h-24"
                      >
                        <circle cx="50" cy="50" r="40" fill="#38915c" />
                        <path
                          d="M 50 50 L 50 10 A 40 40 0 0 1 90 50 Z"
                          fill="#22c55e"
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col items-end gap-1 mt-1 sm:mt-2 text-[10px] sm:text-xs">
                      <div className="flex items-center gap-1 justify-end">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-primary-500 rounded"></div>
                        <span className="text-gray-600">75%</span>
                      </div>
                      <div className="flex items-center gap-1 justify-end">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-secondary-500 rounded"></div>
                        <span className="text-gray-600">25%</span>
                      </div>
                    </div>
                  </div>

                  {/* Floating Card 3 - Profit & Loss (Bottom Left) */}
                  <div className="absolute bottom-6 sm:bottom-10 md:bottom-12 left-3 sm:left-0 sm:-translate-x-[14%] md:-translate-x-[28%] 2xl:-translate-x-[24%] bg-white rounded-lg shadow-xl p-2 sm:p-3 lg:p-4 w-28 sm:w-36 md:w-44 lg:w-48 animate-float-slow z-10">
                    <div className="flex justify-between items-center mb-1 sm:mb-2">
                      <h4 className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-800">
                        P & L
                      </h4>
                      <span className="text-[10px] text-gray-500">Daily</span>
                    </div>
                    <div className="h-10 sm:h-16 md:h-24 bg-gray-50 rounded p-1 sm:p-2">
                      <svg viewBox="0 0 200 100" className="w-full h-full">
                        <rect
                          x="10"
                          y="60"
                          width="20"
                          height="30"
                          fill="#38915c"
                          rx="2"
                        />
                        <rect
                          x="40"
                          y="50"
                          width="20"
                          height="40"
                          fill="#22c55e"
                          rx="2"
                        />
                        <rect
                          x="70"
                          y="45"
                          width="20"
                          height="45"
                          fill="#38915c"
                          rx="2"
                        />
                        <rect
                          x="100"
                          y="55"
                          width="20"
                          height="35"
                          fill="#22c55e"
                          rx="2"
                        />
                        <rect
                          x="130"
                          y="40"
                          width="20"
                          height="50"
                          fill="#38915c"
                          rx="2"
                        />
                        <rect
                          x="160"
                          y="50"
                          width="20"
                          height="40"
                          fill="#22c55e"
                          rx="2"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Courses Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Most Loved Share Market Courses
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto rounded-full"></div>
            </div>

            {/* Carousel Container */}
            <div className="relative min-h-[400px]">
              {/* Enrollment Error Messages */}
              {checkoutError && (
                <div className="mb-6 max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-shake">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold">{checkoutError}</p>
                </div>
              )}

              {loadingCourses ? (
                <PageLoader />
              ) : courses.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-3xl border-2 border-dashed border-gray-200">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <img
                      src={svg.book}
                      alt="Empty"
                      className="w-8 h-8 opacity-50"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    New Courses Coming Soon
                  </h3>
                  <p className="text-gray-500 mt-2">
                    We're updating our curriculum. Check back later!
                  </p>
                </div>
              ) : (
                <>
                  {/* Course Cards Grid */}
                  <div className="overflow-visible py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 4xl:grid-cols-5 gap-6 md:gap-6 lg:gap-8 4xl:gap-10">
                      {courses
                        .slice(
                          currentCourseIndex * coursesPerPage,
                          currentCourseIndex * coursesPerPage + coursesPerPage,
                        )
                        .map((course) => {
                          const isEnrolled =
                            user &&
                            (dbPurchasedSet.has(String(course.id)) ||
                              enrolledSet.has(String(course.id)));
                          return (
                            <CourseCard
                              key={course.id}
                              course={course}
                              isEnrolled={isEnrolled}
                              onEnrol={
                                user
                                  ? isEnrolled
                                    ? undefined
                                    : () => handleEnrollNow(course)
                                  : undefined
                              }
                              viewCoursePath={
                                user
                                  ? `/user/browse/course/${course.id}`
                                  : `/courses/${course.id}`
                              }
                              enrollLoading={checkoutLoadingId === course.id}
                              loginToEnrol={!user}
                            />
                          );
                        })}
                    </div>
                  </div>

                  {/* Carousel Navigation - Centered with Arrows and Dots */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-8">
                      {/* Left Arrow */}
                      <Button
                        variant="icon"
                        onClick={prevCourse}
                        icon={
                          <ChevronLeft size={20} className="text-gray-700" />
                        }
                        className="bg-white shadow-lg hover:shadow-xl border border-gray-200 hover:border-primary-300"
                        aria-label="Previous courses"
                      />

                      {/* Dots/Indicators */}
                      <div className="flex items-center gap-2">
                        {[...Array(totalPages)].map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentCourseIndex(index)}
                            className={`h-2 transition-all duration-300 ${
                              currentCourseIndex === index
                                ? "bg-primary-600 w-8 rounded-full shadow-md"
                                : "bg-gray-300 hover:bg-gray-400 w-2 rounded-full"
                            }`}
                            aria-label={`Go to page ${index + 1}`}
                          />
                        ))}
                      </div>

                      {/* Right Arrow */}
                      <Button
                        variant="icon"
                        onClick={nextCourse}
                        icon={
                          <ChevronRight size={20} className="text-gray-700" />
                        }
                        className="bg-white shadow-lg hover:shadow-xl border border-gray-200 hover:border-primary-300"
                        aria-label="Next courses"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
          <div className="absolute -top-24 -left-20 w-80 h-80 bg-primary-100/30 rounded-full blur-3xl opacity-50"></div>
          <div className="absolute -bottom-24 -right-20 w-80 h-80 bg-secondary-100/30 rounded-full blur-3xl opacity-50"></div>

          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div
              className={`text-center mb-16 transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-10"
              }`}
            >
              <h4 className="text-primary-600 font-bold uppercase tracking-widest text-sm mb-3">
                Student Success Stories
              </h4>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">
                What people say about our courses
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto rounded-full shadow-sm"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {courseReviews.length > 0 ? (
                courseReviews.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`group relative bg-white/80 backdrop-blur-md rounded-2xl p-8 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-700 border border-white hover:border-primary-100 ${
                      isVisible
                        ? "opacity-100 translate-y-0 scale-100"
                        : "opacity-0 translate-y-20 scale-95"
                    }`}
                    style={{
                      transitionDelay: `${index * 150}ms`,
                    }}
                  >
                    {/* Top Accent Bar */}
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Quote Icon - Custom Styled */}
                    <div className="absolute -top-4 -left-4 bg-white shadow-lg rounded-xl p-3 transform -rotate-12 group-hover:rotate-0 transition-transform duration-500 border border-gray-100">
                      <svg
                        className="w-6 h-6 text-primary-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C20.1216 16 21.017 16.8954 21.017 18V21C21.017 22.1046 20.1216 23 19.017 23H16.017C14.9124 23 14.017 22.1046 14.017 21ZM14.017 21C12.9124 21 12.017 20.1046 12.017 19V16.5C12.017 14.0147 14.032 12 14.517 12H17.517C17.032 12 15.017 14.0147 15.017 16.5V19C15.017 20.1046 14.1216 21 14.017 21ZM5.017 21L5.017 18C5.017 16.8954 5.91243 16 7.017 16H10.017C11.1216 16 12.017 16.8954 12.017 18V21C12.017 22.1046 11.1216 23 10.017 23H7.017C5.91243 23 5.017 22.1046 5.017 21ZM5.017 21C3.91243 21 3.017 20.1046 3.017 19V16.5C3.017 14.0147 5.032 12 5.517 12H8.51701C8.03201 12 6.017 14.0147 6.017 16.5V19C6.017 20.1046 5.12157 21 5.017 21Z" />
                      </svg>
                    </div>

                    {/* Rating */}
                    <div className="mb-6 flex items-center justify-between">
                      <StarRating rating={testimonial.rating} />
                      <span className="text-[10px] font-bold bg-primary-50 text-primary-600 px-2 py-1 rounded-full border border-primary-100 uppercase tracking-tighter">
                        Verified
                      </span>
                    </div>

                    {/* Review Text */}
                    <div className="h-28 overflow-hidden mb-6">
                      <p className="text-gray-700 italic leading-relaxed text-sm lg:text-base line-clamp-4 group-hover:text-gray-900 transition-colors">
                        "{testimonial.review}"
                      </p>
                    </div>

                    {/* User Info */}
                    <div className="flex items-center gap-4 pt-6 border-t border-gray-100/50">
                      <div className="relative">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-2xl object-cover ring-2 ring-white shadow-md group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute -bottom-1 -right-1 bg-primary-500 text-white p-1 rounded-lg shadow-sm border-2 border-white">
                          <svg
                            className="w-2 h-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 text-sm xl:text-base truncate group-hover:text-primary-700 transition-colors">
                          {testimonial.name}
                        </h4>
                        <p className="text-[11px] font-semibold text-primary-600 uppercase tracking-wide truncate mt-0.5">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-24 text-center bg-white rounded-[2rem] border-2 border-dashed border-gray-100 shadow-inner group transition-all hover:border-primary-200">
                  <div className="w-24 h-24 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-500">
                    <svg
                      className="w-10 h-10 text-primary-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">
                    New Success Stories Coming Soon
                  </h3>
                  <p className="text-gray-500 mt-4 max-w-md mx-auto leading-relaxed">
                    Our current batch is busy mastering the markets. Their
                    amazing stories will be featured here very soon!
                  </p>
                </div>
              )}
            </div>

            {/* Decorative Elements Footer */}
            <div
              className={`mt-20 text-center transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div className="inline-flex items-center gap-3 bg-white shadow-xl shadow-primary-500/5 px-8 py-4 rounded-2xl border border-gray-100">
                <div className="flex -space-x-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden shadow-sm"
                    >
                      <img
                        src={`https://ui-avatars.com/api/?name=S+${i}&background=random&size=64`}
                        alt="Avatar"
                      />
                    </div>
                  ))}
                </div>
                <div className="w-px h-6 bg-gray-200 mx-2"></div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-yellow-500 fill-yellow-500"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-gray-900 font-bold text-sm lg:text-base">
                    Join {(courseReviews.length || 5) * 100}+ satisfied market
                    mavericks
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Question
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto rounded-full"></div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {Array.isArray(faqItems) && faqItems.length > 0 ? (
                <Accordion items={faqItems} />
              ) : (
                <div className="py-10 px-6 text-center text-gray-600">
                  FAQ is not available
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Dialog
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        title={demoVideo?.title || "Watch Demo"}
        size="xl"
      >
        <div className="space-y-4">
          {demoVideo?.videoUrl ? (
            <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-2xl">
              <video
                src={demoVideo.videoUrl}
                className="w-full h-full"
                controls
                autoPlay
              />
            </div>
          ) : (
            <div className="py-20 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <p className="text-gray-500 font-medium">
                Demo video is currently unavailable.
              </p>
            </div>
          )}
          {demoVideo?.description && (
            <p className="text-gray-600 text-sm leading-relaxed px-2">
              {demoVideo.description}
            </p>
          )}
          {demoVideo?.videoUrl && (
            <a
              href={demoVideo.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-sm font-semibold text-primary-700 hover:text-primary-800 px-2"
            >
              Having playback issues? Open demo in new tab
            </a>
          )}
        </div>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Landing;
